import banner from "../../../courses/module-1/img5.png";

const modulo5 = {
  courseId: "finanzas-personales",
  id: "modulo-5",
  title: "📘 MÓDULO 5: Introducción a la Inversión",
  estimatedDuration: "2 horas",
  format: "Lectura + simulación escrita + evaluación",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos del módulo",
      content: [
        "Comprender qué significa realmente invertir más allá de los mitos.",
        "Descubrir cómo el interés compuesto multiplica tu dinero con el tiempo.",
        "Conocer opciones de inversión seguras y accesibles para jóvenes principiantes.",
        "Aplicar principios de riesgo, rentabilidad y diversificación de forma consciente.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Lectura principal",
      title: "📖 Lectura principal: ¿Qué significa invertir?",
      content: [
        {
          type: "paragraph",
          text: "Invertir es usar parte de tu dinero para ponerlo a trabajar, con la expectativa de que crezca con el tiempo. No se trata de ser millonario de la noche a la mañana, sino de tomar decisiones inteligentes que generen rendimiento futuro.",
        },
        {
          type: "paragraph",
          text: "“Ahorrar es guardar. Invertir es hacer crecer.”",
        },
      ],
    },
    {
      id: "comparacion",
      navLabel: "Ahorro vs Inversión",
      title: "💬 ¿Cómo se diferencia del ahorro?",
      content: [
        {
          type: "table",
          headers: ["Ahorro", "Inversión"],
          rows: [
            ["Protege el dinero", "Hace crecer el dinero"],
            ["Bajo o nulo riesgo", "Implica algún riesgo"],
            ["Liquidez inmediata", "Requiere tiempo para ver resultados"],
            ["Ideal para corto plazo", "Ideal para mediano/largo plazo"],
          ],
        },
      ],
    },
    {
      id: "interes-compuesto",
      navLabel: "Interés Compuesto",
      title: "💸 La Ley del Interés Compuesto: tu superpoder financiero",
      content: [
        {
          type: "paragraph",
          text: "El interés compuesto es la octava maravilla del mundo.” (Albert Einstein).",
        },
        {
          type: "paragraph",
          text: "📌 ¿Qué es?",
        },
        {
          type: "paragraph",
          text: "Es cuando tu dinero genera intereses, y esos intereses también generan intereses. Entre más tiempo dejes que el dinero crezca, más exponencial será el resultado.",
        },
        {
          type: "paragraph",
          text: "🎯 Ejemplo simple:",
        },
        {
          type: "paragraph",
          text: "Si inviertes $1,000 con un rendimiento del 10% anual:",
        },
        {
          type: "list",
          items: [
            "Año 1 → $1,100",
            "Año 2 → $1,210",
            "Año 3 → $1,331",
            "Año 10 → $2,594",
            "Año 20 → $6,727",
          ],
        },
        {
          type: "paragraph",
          text: "La clave no es tener mucho, sino empezar pronto y ser constante.",
        },
      ],
    },
    {
      id: "riesgo-rentabilidad",
      navLabel: "Riesgo y rentabilidad",
      title: "⚖ Ley del Riesgo y Rentabilidad",
      content: [
        {
          type: "paragraph",
          text: "Toda inversión tiene una relación directa entre riesgo y ganancia esperada:",
        },
        {
          type: "table",
          headers: [
            "Tipo inversión",
            "Riesgo",
            "Ganancia potencial",
            "Ejemplo",
          ],
          rows: [
            ["Cuenta de ahorro", "Muy bajo", "Baja", "Banco tradicional"],
            ["CETES", "Bajo", "Moderada", "Gobierno mexicano"],
            ["Fondos de inversión", "Medio", "Mod. a alta", "Fibras, ETF"],
            ["Criptomonedas/acciones", "Alto", "Alta", "Bolsa, Bitcoin"],
          ],
        },
        {
          type: "paragraph",
          text: "A mayor rentabilidad, mayor riesgo. Y viceversa.",
        },
      ],
    },
    {
      id: "reducir-riesgo",
      navLabel: "Reducir riesgo",
      title: "📌 ¿Cómo reducir el riesgo?",
      content: [
        {
          type: "list",
          items: [
            "Invierte solo lo que no necesitas a corto plazo",
            "Diversifica: no pongas todos tus huevos en la misma canasta",
            "Infórmate antes de actuar",
            "Comienza por lo más seguro antes de buscar rendimientos altos",
          ],
        },
      ],
    },
    {
      id: "regla-110",
      navLabel: "Regla 110",
      title: "🔢 Extra: La Regla 110. ¿Cuánto deberías invertir según tu edad?",
      content: [
        {
          type: "paragraph",
          text: "Además de conocer el interés compuesto y la ley riesgo-rentabilidad, existe una fórmula sencilla para saber qué porcentaje de tu dinero puedes poner en inversiones de mayor riesgo.",
        },
        {
          type: "paragraph",
          text: "¿Qué dice la Regla 110?",
        },
        {
          type: "paragraph",
          text: "110 – tu edad = porcentaje recomendado para instrumentos con mayor volatilidad",
        },
        {
          type: "paragraph",
          text: "👇 Ejemplo práctico:",
        },
        {
          type: "list",
          items: ["Si tienes 20 años:"],
        },
        "110 – 20 = 90",
        "→ hasta 90% de tu portafolio podría ir a activos más arriesgados (acciones, criptos, ETFs).",
        {
          type: "list",
          items: ["Si tienes 30 años:"],
        },
        "110 – 30 = 80",
        "→ un perfil con 80% en riesgo y 20% en inversiones seguras.",
        {
          type: "paragraph",
          text: "No es una regla estricta, sino una guía útil para crear una estrategia equilibrada según tu edad.",
        },
      ],
    },
    {
      id: "ejercicio",
      navLabel: "Ejercicio práctico",
      title: "🧠 Ejercicio práctico: simulación de crecimiento",
      content: [
        {
          type: "paragraph",
          text: "Imagina que puedes separar $200 pesos al mes para invertir en CETES (3 meses) con una tasa promedio del 9% anual.",
        },
        {
          type: "paragraph",
          text: "¿Cuánto tendrías en 1, 3 y 5 años si reinviertes todo lo ganado cada vez?",
        },
        {
          type: "paragraph",
          text: "Usa la fórmula del interés compuesto:",
        },
        {
          type: "list",
          items: [
            "Monto final = Inversión × (1 + tasa anual / 12)^(12 × años)",
          ],
        },
        {
          type: "paragraph",
          text: "Ejercicio escrito:",
        },
        "Calcula y anota cuánto crecería tu dinero en 3 escenarios:",
        {
          type: "list",
          items: ["Invertir 1 año", "Invertir 3 años", "Invertir 5 años"],
        },
        "Reflexiona:",
        {
          type: "list",
          items: [
            "¿Te motiva más ahorrar o ver tu dinero crecer?",
            "¿Qué nivel de riesgo estás dispuesto a asumir según tu edad?",
          ],
        },
      ],
    },
    {
      id: "ejemplo-real",
      navLabel: "Ejemplo real",
      title: "💬 Ejemplo real joven",
      content: [
        {
          type: "paragraph",
          text: "🎓 Isaac, 21 años, estudiante en Querétaro, empezó a invertir $500 pesos al mes en CETES desde los 19. Hoy, dos años después, ya tiene más de $13,000 acumulados sin haber hecho grandes sacrificios.",
        },
        {
          type: "paragraph",
          text: "Lo hizo desde su celular, sin complicaciones, con constancia y paciencia.",
        },
        {
          type: "paragraph",
          text: "“Invertir me hizo sentir que tengo el control de mi futuro, aunque empiece poco a poco.”",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Miniquiz de autoevaluación",
      questions: [
        {
          id: "q1",
          question: "¿Qué diferencia principal hay entre ahorrar e invertir?",
          options: [
            { key: "a", text: "Invertir es más seguro" },
            { key: "b", text: "Invertir busca hacer crecer el dinero" },
            { key: "c", text: "Ahorrar es ilegal si tienes tarjeta" },
            { key: "d", text: "Ahorrar siempre pierde valor" },
          ],
          correct: "b",
        },
        {
          id: "q2",
          question: "¿Qué es el interés compuesto?",
          options: [
            { key: "a", text: "Cuando ganas más por gastar" },
            { key: "b", text: "Cuando los intereses generan más intereses" },
            { key: "c", text: "Un impuesto del SAT" },
            { key: "d", text: "Una técnica bancaria para perder dinero" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "¿Qué representa la Ley de Riesgo y Rentabilidad?",
          options: [
            { key: "a", text: "A mayor ganancia, mayor riesgo" },
            { key: "b", text: "Todas las inversiones son seguras" },
            { key: "c", text: "Siempre se gana lo mismo" },
            { key: "d", text: "El ahorro no tiene riesgo" },
          ],
          correct: "a",
        },
        {
          id: "q4",
          question: "¿Qué puedes hacer para reducir el riesgo?",
          options: [
            { key: "a", text: "Diversificar y empezar por lo seguro" },
            { key: "b", text: "Apostar todo a cripto" },
            { key: "c", text: "Gastar antes de invertir" },
            { key: "d", text: "Ignorar los contratos" },
          ],
          correct: "a",
        },
        {
          id: "q5",
          question: "¿Qué ventaja tiene empezar joven a invertir?",
          options: [
            { key: "a", text: "Más tiempo para crecer tu dinero" },
            { key: "b", text: "No pagar impuestos" },
            { key: "c", text: "Tener más tarjetas" },
            { key: "d", text: "Menos deuda estudiantil" },
          ],
          correct: "a",
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografía recomendada",
      title: "📚 Bibliografía recomendada",
      content: [
        "Sofía Macías – ‘Pequeño Cerdo Capitalista: Inversiones’",
        "CONDUSEF – ‘Guía de inversiones básicas’",
        "Blog ‘El Arte de Invertir Joven’ – educacionfinancieramx.org.mx",
      ],
    },
    {
      id: "frase",
      navLabel: "Frase del día",
      title: "🧠 Frase del día",
      content: [
        "El mejor momento para invertir fue ayer. El segundo mejor momento es hoy.",
      ],
    },
  ],
};

export default modulo5;
